<template>
  <PriorHeader />
  <v-container class="patient-information-form-wrapper">
    <v-row>
      <v-col cols="12">
        <h1 class="text-primary weight-600 pb-4">Submission Details</h1>

        <div v-if="showPreloader">
          <!-- Skeleton for patient information form -->
          <v-card class="mb-6 submission-detail-section">
            <v-card-title>
              <v-skeleton-loader type="heading" width="60%"></v-skeleton-loader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col v-for="i in 4" :key="`patient-${i}`" cols="12" md="6">
                  <v-skeleton-loader type="list-item-avatar-two-line" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Skeleton for pharmacy information -->
          <v-card class="mb-6 submission-detail-section">
            <v-card-title>
              <v-skeleton-loader type="heading" width="40%"></v-skeleton-loader>
            </v-card-title>
            <v-card-text>
              <v-skeleton-loader type="paragraph" class="mb-4"></v-skeleton-loader>
              <v-skeleton-loader type="list-item-three-line"></v-skeleton-loader>
            </v-card-text>
          </v-card>

          <!-- Skeleton for diagnosis information -->
          <v-card class="mb-6 submission-detail-section">
            <v-card-title>
              <v-skeleton-loader type="heading" width="50%"></v-skeleton-loader>
            </v-card-title>
            <v-card-text>
              <v-skeleton-loader type="heading" width="30%" class="mb-4"></v-skeleton-loader>
              <v-skeleton-loader type="list-item-three-line"></v-skeleton-loader>
              <v-skeleton-loader type="list-item-two-line" class="mt-4"></v-skeleton-loader>
            </v-card-text>
          </v-card>

          <!-- Skeleton for step therapies -->
          <v-card class="mb-6 submission-detail-section">
            <v-card-title>
              <v-skeleton-loader type="heading" width="45%"></v-skeleton-loader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col v-for="i in 2" :key="`therapy-${i}`" cols="12" md="6">
                  <v-skeleton-loader type="list-item-avatar-three-line" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Skeleton for supporting documents -->
          <v-card class="mb-6 submission-detail-section">
            <v-card-title>
              <v-skeleton-loader type="heading" width="55%"></v-skeleton-loader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col v-for="i in 3" :key="`doc-${i}`" cols="12">
                  <v-skeleton-loader type="list-item-avatar-two-line" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Skeleton for questions -->
          <v-card class="mb-6 submission-detail-section">
            <v-card-title>
              <v-skeleton-loader type="heading" width="35%"></v-skeleton-loader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col v-for="i in 4" :key="`question-${i}`" cols="12">
                  <v-skeleton-loader type="list-item-three-line" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <v-row v-if="orderInfo">
          <v-row class="submission-status-wrapper" style="margin-bottom: 12px">
            <v-col cols="12" class="lh--available-orders-container">
              <SubmissionDetailCard :order="orderInfo" :buttons-visible="false" :fax-forms="faxDestinationOptions" />
            </v-col>
          </v-row>

          <v-row class="submission-status-wrapper" style="margin-bottom: 12px">
            <v-col cols="12" class="lh--available-orders-container">
              <PolicyReviewCard :order="orderInfo" />
            </v-col>
          </v-row>

          <v-col cols="12" class="submission-detail-section">
            <PatientInformationFormPreview :order-info="orderInfo" />
          </v-col>

          <v-col cols="12" class="submission-detail-section">
            <ProviderInformationFormPreview :order-info="orderInfo" />
          </v-col>

          <v-col cols="12" class="submission-detail-section">
            <MedicalInformationForm :order-info="orderInfo" />
          </v-col>

          <!-- <v-col v-if="orderInfo.pharmacyInfo" cols="12" class="submission-detail-section">
            <div class="form">
              <div class="patient-information-part">
                <h5>
                  Pharmacy Information <span v-if="orderInfo.benefitType === 'Medical benefits'">(Optional)</span>
                </h5>
                <hr class="bold-hr" />
                <p>
                  By providing the pharmacy information, you authorize Do Prior Auth to contact the pharmacy of your
                  choice on your behalf to report the status of your Prior Authorization as well as follow-up.
                </p>
                <PharmacyLookupComponent :pharmacy-selection="orderInfo.pharmacyInfo" />
              </div>
            </div>
          </v-col> -->

          <v-col
            v-if="orderInfo.primaryDiagnosisName && orderInfo.primaryDiagnosisIcd10Code"
            cols="12"
            class="submission-detail-section">
            <v-row class="patient-information-container">
              <v-col cols="12">
                <v-row class="patient-information">
                  <v-col cols="12" class="header-with-select-box">
                    <h3 class="text-primary weight-600">Patient Diagnosis Information</h3>
                  </v-col>

                  <v-col cols="12" class="text-body-3-1 weight-600 text-primary">PRIMARY DIAGNOSIS</v-col>

                  <v-col cols="12">
                    <DiagnosisSelectionPreviewComponent
                      :order-key="orderInfo.orderKey"
                      :primary-diagnosis-name="orderInfo.primaryDiagnosisName"
                      :primary-diagnosis-icd10-code="orderInfo.primaryDiagnosisIcd10Code"
                      :secondary-diagnosis="orderInfo.secondaryDiagnosis"
                      :medication="orderInfo.medication" />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" class="submission-detail-section">
            <SupportingDocumentsPreviewComponent
              :order-key="orderInfo.orderKey"
              :medical-necessity="orderInfo.medicalNecessity"
              :medical-necessity-template="orderInfo.medicalNecessityTemplate"
              :patient-chart="orderInfo.patientChart" />
          </v-col>
          <v-col v-if="!orderInfo.editableSubmissionForm" cols="12" class="submission-detail-section">
            <QuestionsPreviewComponent
              :title="basicPlusQuestionsTitle"
              :checklist="orderInfo.basicPlusChecklist"
              :llm-prediction="orderInfo.basicPlusChecklistLlmScores"
              :original-payer-questions="orderInfo.basicPlusQuestions" />
          </v-col>
          <v-col v-if="filledFormPdfBlob" cols="12" class="submission-detail-section">
            <FlattenedPdfComponent :pdf-form="filledFormPdfBlob" />
          </v-col>
          <v-col cols="12" class="submission-detail-section">
            <QuestionsPreviewComponent
              :title="payerQuestionsTitle"
              :checklist="orderInfo.checklist"
              :llm-prediction="orderInfo.checklistLlmScores"
              :policy-reviews="orderInfo.checklistPolicyReviews"
              :original-payer-questions="orderInfo.payerQuestions" />
          </v-col>
          <v-col cols="12" class="submission-detail-section">
            <ExpeditedReviewComponent
              :expedited-review-within24h="orderInfo.expeditedReviewWithin24h"
              :expedited-review-statement="orderInfo.expeditedReviewStatement" />
          </v-col>
          <v-col cols="12" class="submission-detail-section">
            <v-row class="section-container lh--signature-container">
              <v-col cols="12" class="header-with-select-box">
                <h3 class="text-primary weight-600">Signature</h3>
                <v-text-field :disabled="true">{{ orderInfo.signature }}</v-text-field>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <br />
        <AuditLogList :order-info-id="route.params.id" />
      </v-col>
    </v-row>
  </v-container>
  <PriorFooter />
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { orderInfoService } from "@/services/orderInfoService";
import { mainServices } from "@/services/mainServices";

import PriorHeader from "@/components/PriorHeader";
import PriorFooter from "@/components/PriorFooter";
import PatientInformationFormPreview from "@/pages/PatientInformationFormPreview";
import DiagnosisSelectionPreviewComponent from "@/components/DiagnosisSelectionPreviewComponent.vue";
import ExpeditedReviewComponent from "@/components/ExpeditedReviewComponent.vue";
import SupportingDocumentsPreviewComponent from "@/components/SupportingDocumentsPreviewComponent.vue";
import QuestionsPreviewComponent from "@/components/QuestionsPreviewComponent";
import SubmissionDetailCard from "@/components/SubmissionDetailCard.vue";
import AuditLogList from "@/components/AuditLogList.vue";
// import PharmacyLookupComponent from "@/components/PharmacyLookupComponent";
import FlattenedPdfComponent from "@/components/FlattenedPdfComponent.vue";
import { tryParseApiErrors } from "@/utils";
import { useSelectedSubmissionStore } from "@/stores/selectedSubmissionStore";
import { storeToRefs } from "pinia";
import ProviderInformationFormPreview from "./ProviderInformationFormPreview.vue";
import MedicalInformationForm from "./MedicalInformationForm.vue";
import PolicyReviewCard from "@/components/PolicyReviewCard.vue";

const { selectedSubmission } = storeToRefs(useSelectedSubmissionStore());

const route = useRoute();
const errors = ref([]);
const showPreloader = ref(false);
const orderInfo = ref(null);
const filledFormPdfBlob = ref(null);
const faxDestinationOptions = ref([]);

const payerQuestionsTitle = "Insurance Coverage Criteria";
const basicPlusQuestionsTitle = "Additional Questions";

onMounted(() => {
  if (route.params.id) {
    getOrderInfo(route.params.id);
  }
  getFaxForms();
});

onUnmounted(() => {
  clearSelectedSubmissionStorage();
});

async function getFaxForms() {
  faxDestinationOptions.value = await mainServices.getFaxForms();
}

async function getOrderInfo(id) {
  showPreloader.value = true;
  try {
    let submissionInfo = await orderInfoService.getOrderDetails(id);
    orderInfo.value = submissionInfo.orderInfo;

    if (orderInfo.value.editableSubmissionForm) {
      const pdfResponse = await orderInfoService.getFilledFormPdf(id);
      filledFormPdfBlob.value = pdfResponse.data;
    }
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  showPreloader.value = false;
}

function clearSelectedSubmissionStorage() {
  selectedSubmission.value = {};
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";

.mb-6 {
  margin-bottom: 24px;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.mb-2 {
  margin-bottom: 8px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mt-4 {
  margin-top: 16px;
}
</style>
